// Entidad Jugador: explorador espacial con controles suaves y tolerantes para niños.
// Incluye control aéreo total, coyote time y buffer de salto para que brincar se sienta fácil.

import Phaser from 'phaser';
import { PHYS, HEX } from '../config';
import { ProgressData as PlayerData } from '../systems/ProgressData';
import { getMode } from '../util/responsive';

// El arte puede crecer sin modificar el cuerpo Arcade: conserva exactamente la
// dificultad, las colisiones y el salto aprobados en todos los dispositivos.
const PLAYER_BODY = Object.freeze({
  width: 64 * 0.62,
  height: 95 * 0.80,
});
const PLAYER_GROUND_OFFSET = 95 * 0.50;

// Margen transparente inferior real de cada SVG (en píxeles de la fuente).
// Solo corrige dónde se ven las botas; no modifica el cuerpo Arcade.
const PLAYER_VISUAL_GROUNDING = Object.freeze({
  explorador: Object.freeze({ idle: 8, run: 26 }),
  exploradora: Object.freeze({ idle: 15, run: 31 }),
});

function getVisualSize(scaleManager) {
  const W = scaleManager.width;
  const H = scaleManager.height;

  // Teléfonos horizontales: mayor presencia, sin invadir HUD, controles o plataformas.
  if (H <= 460 && W <= 932) return { width: 72, height: 107, glow: 1.42 };
  // Tablet: refuerza el protagonista sin alterar la zona de juego.
  if (getMode(W) === 'tablet' || (W <= 1180 && H <= 900)) return { width: 78, height: 116, glow: 1.50 };
  // Desktop: la mejora visual principal (+28 % aprox. frente a 64 × 95).
  return { width: 82, height: 122, glow: 1.58 };
}

const PROFILE_COLORS = {
  cyan: HEX.cyan,
  violet: HEX.violet,
  magenta: 0xff4ed8,
  gold: HEX.gold,
  green: 0x4dff9e,
};

export class Player extends Phaser.Physics.Arcade.Sprite {
  constructor(scene, x, y) {
    const profile = PlayerData.getProfile();
    const characterId = profile.explorer === 'exploradora' ? 'exploradora' : 'explorador';
    const texture = characterId === 'exploradora' ? 'player_exploradora' : 'player_official';
    super(scene, x, y, texture);
    this.characterId = characterId;
    scene.add.existing(this);
    scene.physics.add.existing(this);

    this.setDepth(10);
    // Conservamos estas referencias mientras la escena está viva. Durante destroy()
    // Phaser puede invalidar this.scene antes de que se ejecute la limpieza.
    this.scaleManager = scene.scale;
    this.sceneEvents = scene.events;
    this.visualSize = getVisualSize(this.scaleManager);
    this.currentPose = 'idle';
    this.setDisplaySize(this.visualSize.width, this.visualSize.height);
    this.setOrigin(0.5, this.getVisualOriginY());
    this.idleTexture = texture;
    this.runTexture = profile.explorer === 'exploradora'
      ? 'player_exploradora_run'
      : 'player_official_run';
    this.syncBodyToArt();
    this.setCollideWorldBounds(false);

    this.handlePlayerResize = () => {
      if (!this.active || !this.scaleManager) return;
      this.applyVisualSize();
    };
    this.handleSceneShutdown = () => this.cleanupResizeListener();
    this.scaleManager.on('resize', this.handlePlayerResize);
    this.sceneEvents.once('shutdown', this.handleSceneShutdown);

    this.coyote = 0;
    this.buffer = 0;
    this.jumpHeld = false;
    this.cutApplied = false;
    this.facing = 1;
    this.mirroredBaseArt = profile.explorer === 'exploradora';

    // El retrato se mantiene reconocible; la energía del traje refleja el color elegido en el perfil.
    this.profileColor = PROFILE_COLORS[profile.color] || HEX.cyan;

    // Aura de energía alrededor del héroe.
    this.glow = scene.add
      .image(x, y, 'glow')
      .setTint(this.profileColor)
      .setScale(this.visualSize.glow)
      .setAlpha(0.55)
      .setDepth(9)
      .setBlendMode(Phaser.BlendModes.ADD);

    // Estela de partículas al correr / brincar.
    this.trail = scene.add.particles(0, 0, 'spark', {
      follow: this,
      followOffset: { x: 0, y: 18 },
      speed: { min: 10, max: 40 },
      scale: { start: 0.5, end: 0 },
      alpha: { start: 0.7, end: 0 },
      lifespan: 350,
      frequency: 60,
      tint: [this.profileColor, HEX.violet],
      blendMode: 'ADD',
    });
    this.trail.setDepth(8);
    this.trail.stop();
  }

  onGround() {
    return this.body.blocked.down || this.body.touching.down;
  }

  handleInput(input, dtMs) {
    const { left, right, jumpDown, jumpHeld } = input;

    // Movimiento horizontal (con control aéreo total).
    let vx = 0;
    if (left) vx -= PHYS.MOVE;
    if (right) vx += PHYS.MOVE;
    this.setVelocityX(vx);

    const running = vx !== 0;
    if (running) this.facing = vx > 0 ? 1 : -1;
    this.setPose(running ? 'run' : 'idle');
    this.updateFacing(running);

    const grounded = this.onGround();
    if (grounded) {
      this.coyote = PHYS.COYOTE_MS;
      this.cutApplied = false;
    } else {
      this.coyote = Math.max(0, this.coyote - dtMs);
    }

    if (jumpDown) this.buffer = PHYS.BUFFER_MS;
    else this.buffer = Math.max(0, this.buffer - dtMs);

    if (this.buffer > 0 && this.coyote > 0) {
      this.setVelocityY(-PHYS.JUMP);
      this.buffer = 0;
      this.coyote = 0;
      if (this.onJump) this.onJump();
    }

    // Salto variable: soltar pronto = brinco más corto.
    if (!jumpHeld && !this.cutApplied && this.body.velocity.y < -120) {
      this.setVelocityY(this.body.velocity.y * PHYS.JUMP_CUT);
      this.cutApplied = true;
    }

    // La pose de carrera ya expresa el movimiento; evitar inclinación o estiramiento artificial.
    this.rotation = 0;

    // Estela solo cuando hay movimiento notable.
    if ((Math.abs(vx) > 10 && grounded) || !grounded) this.trail.start();
    else this.trail.stop();
  }

  applyVisualSize() {
    if (!this.active || !this.scaleManager) return;
    this.visualSize = getVisualSize(this.scaleManager);
    this.setDisplaySize(this.visualSize.width, this.visualSize.height);
    // Mantiene el cuerpo físico en su punto aprobado y corrige solo el
    // margen transparente del SVG para que las botas se vean apoyadas.
    this.setOrigin(0.5, this.getVisualOriginY());
    if (this.glow) this.glow.setScale(this.visualSize.glow);
    this.syncBodyToArt();
  }

  getVisualBottomPadding() {
    return PLAYER_VISUAL_GROUNDING[this.characterId]?.[this.currentPose] || 0;
  }

  getVisualOriginY() {
    const sourceHeight = this.frame.realHeight || this.height || 768;
    return 1
      - (PLAYER_GROUND_OFFSET / this.visualSize.height)
      - (this.getVisualBottomPadding() / sourceHeight);
  }

  syncBodyToArt() {
    // El cuerpo Arcade conserva exactamente su tamaño y su apoyo en las botas,
    // aun cuando la ilustración cambie entre idle, run o tamaño de pantalla.
    const scaleX = Math.abs(this.scaleX) || 1;
    const scaleY = Math.abs(this.scaleY) || 1;
    this.body.setSize(PLAYER_BODY.width / scaleX, PLAYER_BODY.height / scaleY, false);
    this.body.setOffset(
      (this.displayWidth - PLAYER_BODY.width) / (2 * scaleX),
      // Compensa el origin visual: el body conserva idéntico bottom world-space.
      (this.displayHeight - PLAYER_BODY.height) / scaleY - this.getVisualBottomPadding(),
    );
  }

  setPose(pose) {
    if (this.currentPose === pose) return;
    this.currentPose = pose;
    this.setTexture(pose === 'run' ? this.runTexture : this.idleTexture);
    this.applyVisualSize();
  }

  updateFacing(running) {
    // Las poses de carrera miran a la derecha; la ilustración quieta de la exploradora mira a la izquierda.
    const baseFacesRight = running || !this.mirroredBaseArt;
    this.setFlipX(baseFacesRight ? this.facing < 0 : this.facing > 0);
  }

  preUpdate(t, d) {
    super.preUpdate(t, d);
    this.glow.setPosition(this.x, this.y);
  }

  cleanupResizeListener() {
    if (this.scaleManager && this.handlePlayerResize) {
      this.scaleManager.off('resize', this.handlePlayerResize);
    }
    if (this.sceneEvents && this.handleSceneShutdown) {
      this.sceneEvents.off('shutdown', this.handleSceneShutdown);
    }
    this.handlePlayerResize = null;
    this.handleSceneShutdown = null;
    this.scaleManager = null;
    this.sceneEvents = null;
  }

  destroy(fromScene) {
    this.cleanupResizeListener();
    if (this.glow) this.glow.destroy();
    if (this.trail) this.trail.destroy();
    super.destroy(fromScene);
  }
}
